import type {ReactNode} from 'react'
import {cn} from '@/lib/utils'

type ScrollableBoxMaxHeight = 'sm' | 'md' | 'lg' | 'xl'

interface ScrollableBoxProps {
    children: ReactNode
    maxHeight?: ScrollableBoxMaxHeight
    /**
     * When `true`, the container grows to fill its parent (`flex-1`) instead of
     * using a fixed max-height. Use inside a flex-col master panel so the list
     * expands to take whatever vertical space the toolbar and footer leave.
     */
    grow?: boolean
    /** Render without the default border + rounded corners. Useful when nested inside an already-bordered frame. */
    flush?: boolean
    /** Override default `overflow-y-auto` with `overflow-hidden` (useful for editors that manage their own scrolling). */
    hideOverflow?: boolean
    /** Skip applying any height constraint (no max-height, no flex-1). Caller controls height via className. */
    autoHeight?: boolean
    className?: string
    'data-testid'?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const maxHeightClasses: Record<ScrollableBoxMaxHeight, string> = {
    sm: 'max-h-48',
    md: 'max-h-64',
    lg: 'max-h-96',
    xl: 'max-h-[600px]',
}
/* eslint-enable i18next/no-literal-string */

export function ScrollableBox({
    children,
    maxHeight = 'md',
    grow,
    flush,
    hideOverflow,
    autoHeight,
    className,
    'data-testid': testId,
}: ScrollableBoxProps) {
    return (
        <div
            className={cn(
                hideOverflow ? 'overflow-hidden' : 'overflow-y-auto',
                !flush && 'border rounded-md',
                !autoHeight && (grow ? 'flex-1' : maxHeightClasses[maxHeight]),
                className,
            )}
            data-testid={testId}
        >
            {children}
        </div>
    )
}
